/**
 * LandingPage.jsx - Garage Public Landing Page
 * 
 * This is the public-facing page for each garage (accessed via /:garageSlug).
 * Shows garage-specific info: cover image, tagline, address, call button,
 * services, and parts listings.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { formatCurrency } from '../lib/i18n';
import {
  Wrench,
  Shield,
  Clock,
  Star,
  ChevronRight,
  Phone,
  MapPin,
  Navigation,
  ExternalLink,
  Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPageLegacy() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  
  // State for garage data
  const [services, setServices] = useState([]);
  const [parts, setParts] = useState([]);
  const [garageInfo, setGarageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [badgeOpacity, setBadgeOpacity] = useState(1);
  const scrollTimer = useRef(null);

  // Scroll-fade for watermark badge
  useEffect(() => {
    const onScroll = () => {
      setBadgeOpacity(0.15);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setBadgeOpacity(1), 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (scrollTimer.current) clearTimeout(scrollTimer.current); };
  }, []);

  // Fetch garage data on mount
  useEffect(() => {
    if (garageSlug) {
      fetchGarageData();
    }
  }, [garageSlug]);

  const fetchGarageData = async () => {
    try {
      const [garageRes, servicesRes, partsRes] = await Promise.all([
        axios.get(`${API}/tenants/${garageSlug}/public`),
        axios.get(`${API}/tenants/${garageSlug}/services`),
        axios.get(`${API}/tenants/${garageSlug}/parts`)
      ]);
      setGarageInfo(garageRes.data);
      setServices(servicesRes.data.slice(0, 6));
      setParts(partsRes.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch garage data:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect to main landing if no slug
  if (!garageSlug) {
    navigate('/');
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // 404 state
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">
          {language === 'en' ? 'Garage Not Found' : 'গ্যারেজ পাওয়া যায়নি'}
        </h1>
        <p className="text-slate-600 mb-8">
          {language === 'en' 
            ? "The garage you're looking for doesn't exist or is not active."
            : 'আপনি যে গ্যারেজটি খুঁজছেন সেটি বিদ্যমান নেই বা সক্রিয় নয়।'}
        </p>
        <Link to="/">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            {language === 'en' ? 'Go to MotoPotti' : 'মোটোপটিতে যান'}
          </Button>
        </Link>
      </div>
    );
  }

  // Garage info with defaults
  const garage = garageInfo || {};
  const garageName = language === 'bn' && garage.name_bn ? garage.name_bn : garage.name;
  const tagline = language === 'bn' && garage.tagline_bn ? garage.tagline_bn : garage.tagline;
  const description = language === 'bn' && garage.description_bn ? garage.description_bn : garage.description;
  const address = language === 'bn' && garage.address_bn ? garage.address_bn : garage.address;
  const basePath = `/${garageSlug}`;
  
  // Handle image URLs (can be relative /uploads/... or absolute https://...)
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/') ? `${BASE_URL}${url}` : url;
  };
  
  // Default cover image if none set
  const coverImage = getImageUrl(garage.cover_image_url) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920';

  // Features list
  const features = [
    { icon: Wrench, title: language === 'en' ? 'Expert Technicians' : 'দক্ষ টেকনিশিয়ান', desc: language === 'en' ? 'Certified mechanics with years of experience' : 'অভিজ্ঞ সার্টিফাইড মেকানিক' },
    { icon: Shield, title: language === 'en' ? 'Quality Parts' : 'মানসম্মত পার্টস', desc: language === 'en' ? 'Only genuine and high-quality spare parts' : 'শুধুমাত্র আসল ও উচ্চমানের পার্টস' },
    { icon: Clock, title: language === 'en' ? 'Quick Service' : 'দ্রুত সার্ভিস', desc: language === 'en' ? 'Same-day service for most repairs' : 'অধিকাংশ মেরামতে একই দিনে সার্ভিস' },
    { icon: Star, title: language === 'en' ? 'Best Prices' : 'সেরা দাম', desc: language === 'en' ? 'Competitive pricing guaranteed' : 'প্রতিযোগিতামূলক দাম নিশ্চিত' },
  ];

  // Open Google Maps with directions
  const openDirections = () => {
    if (garage.latitude && garage.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${garage.latitude},${garage.longitude}`, '_blank');
    } else if (address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    }
  };

  // Make phone call
  const callGarage = () => {
    window.location.href = `tel:${garage.phone}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Name */}
            <Link to={basePath} className="flex items-center gap-2" data-testid="logo-link">
              {garage.logo_url ? (
                <img src={garage.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-slate-900 tracking-tight font-['Barlow_Condensed']">
                {garageName}
              </span>
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <Link to={basePath} className="text-sm font-medium text-blue-800">
                {language === 'en' ? 'Home' : 'হোম'}
              </Link>
              <Link to={`${basePath}/services`} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Services' : 'সার্ভিস'}
              </Link>
              <Link to={`${basePath}/store`} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Parts' : 'পার্টস'}
              </Link>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2" data-testid="language-toggle">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'বাংলা' : 'English'}</span>
              </Button>
              <Button onClick={callGarage} className="bg-blue-800 hover:bg-blue-900 gap-2" data-testid="call-btn">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'Call Now' : 'কল করুন'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Cover Image */}
      <section className="relative min-h-[85vh] flex items-center pt-16">
        {/* Cover Image Background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${coverImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        {/* Blue Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-blue-900/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            {/* Garage Name */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase tracking-tight" data-testid="garage-name">
              {garageName}
            </h1>
            
            {/* Tagline */}
            {tagline && (
              <p className="text-xl md:text-2xl text-blue-100 mb-6 font-light" data-testid="tagline">
                {tagline}
              </p>
            )}
            
            {/* Description */}
            {description && (
              <p className="text-slate-200 mb-8 leading-relaxed max-w-xl">
                {description}
              </p>
            )}

            {/* Address with Map Button */}
            {address && (
              <div className="flex items-start gap-3 mb-8 text-white/90">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-lg">{address}</p>
                  <Button 
                    variant="link" 
                    onClick={openDirections}
                    className="text-orange-400 hover:text-orange-300 p-0 h-auto font-medium gap-1"
                    data-testid="directions-btn"
                  >
                    <Navigation className="w-4 h-4" />
                    {language === 'en' ? 'Get Directions' : 'দিকনির্দেশনা পান'}
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                onClick={callGarage}
                className="bg-orange-500 hover:bg-orange-600 text-white h-14 px-8 text-lg font-bold uppercase tracking-wide gap-2"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                {garage.phone}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={openDirections}
                className="text-white border-white hover:bg-white/10 h-14 px-8 text-lg gap-2"
                data-testid="hero-directions-btn"
              >
                <Navigation className="w-5 h-5" />
                {language === 'en' ? 'Get Directions' : 'দিকনির্দেশনা'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
              {language === 'en' ? 'Why Choose Us' : 'কেন আমাদের বেছে নেবেন'}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-blue-800" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      {services.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
                {language === 'en' ? 'Our Services' : 'আমাদের সার্ভিস'}
              </h2>
              <Link to={`${basePath}/services`} data-testid="view-all-services">
                <Button variant="outline" className="gap-2">
                  {language === 'en' ? 'View All' : 'সব দেখুন'}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Card key={service.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-lg text-slate-900">
                        {language === 'bn' && service.name_bn ? service.name_bn : service.name}
                      </h3>
                      <Badge variant="secondary">{service.category}</Badge>
                    </div>
                    <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-blue-800">
                        {formatCurrency(service.price, language)}
                      </span>
                      <span className="text-sm text-slate-500">{service.estimated_time}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Parts Section */}
      {parts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
                {language === 'en' ? 'Spare Parts' : 'খুচরা যন্ত্রাংশ'}
              </h2>
              <Link to={`${basePath}/store`} data-testid="view-all-parts">
                <Button variant="outline" className="gap-2">
                  {language === 'en' ? 'View All' : 'সব দেখুন'}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {parts.map((part) => (
                <Card key={part.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <Badge className="mb-2 bg-slate-100 text-slate-700">{part.brand || part.category}</Badge>
                    <h3 className="font-bold text-slate-900 mb-1">
                      {language === 'bn' && part.name_bn ? part.name_bn : part.name}
                    </h3>
                    <p className="text-lg font-bold text-blue-800">
                      {formatCurrency(part.selling_price || part.price, language)}
                    </p>
                    {(part.stock_quantity || part.quantity) > 0 ? (
                      <Badge className="mt-2 bg-green-100 text-green-800">
                        {language === 'en' ? 'In Stock' : 'স্টকে আছে'}
                      </Badge>
                    ) : (
                      <Badge className="mt-2 bg-red-100 text-red-800">
                        {language === 'en' ? 'Out of Stock' : 'স্টক শেষ'}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MotoPotti Floating Badge — iOS Liquid Glass */}
      <Link
        to="/"
        className="fixed bottom-5 right-5 z-50 group flex items-center gap-1.5 hover:gap-2.5 rounded-full py-1.5 pl-1.5 pr-2.5 hover:pr-3.5 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer"
        style={{
          opacity: badgeOpacity,
          transition: 'opacity 0.5s ease, gap 0.5s cubic-bezier(0.34,1.56,0.64,1), padding 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.45) 40%, rgba(240,249,255,0.55) 100%)',
          backdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          WebkitBackdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          border: '0.5px solid rgba(255,255,255,0.7)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06), 0 1.5px 6px rgba(0,0,0,0.03), inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -0.5px 0 rgba(0,0,0,0.04)',
        }}
        title="MotoPotti"
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: 'linear-gradient(145deg, #34d399 0%, #0d9488 100%)',
            boxShadow: '0 2px 8px rgba(13,148,136,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <Wrench className="w-3 h-3 text-white drop-shadow-sm" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-[9px] text-slate-400/70 font-medium">{language === 'en' ? 'Powered by' : 'পরিচালিত'}</span>
          <span className="text-[12px] font-bold text-slate-600 tracking-tight">MotoPotti</span>
        </div>
      </Link>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Garage Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                {garage.logo_url ? (
                  <img src={garage.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Wrench className="w-6 h-6 text-white" />
                  </div>
                )}
                <span className="text-xl font-bold font-['Barlow_Condensed']">{garageName}</span>
              </div>
              {tagline && <p className="text-slate-400 text-sm">{tagline}</p>}
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold mb-4">{language === 'en' ? 'Contact' : 'যোগাযোগ'}</h4>
              <div className="space-y-2 text-slate-400">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {garage.phone}
                </p>
                {address && (
                  <p className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-1" />
                    {address}
                  </p>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold mb-4">{language === 'en' ? 'Quick Links' : 'দ্রুত লিংক'}</h4>
              <div className="space-y-2">
                <Link to={`${basePath}/services`} className="block text-slate-400 hover:text-white">
                  {language === 'en' ? 'Services' : 'সার্ভিস'}
                </Link>
                <Link to={`${basePath}/store`} className="block text-slate-400 hover:text-white">
                  {language === 'en' ? 'Spare Parts' : 'খুচরা যন্ত্রাংশ'}
                </Link>
                <button onClick={openDirections} className="block text-slate-400 hover:text-white">
                  {language === 'en' ? 'Get Directions' : 'দিকনির্দেশনা'}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} {garageName}. {language === 'en' ? 'All rights reserved.' : 'সর্বস্বত্ব সংরক্ষিত।'}</p>
            <p className="mt-2 md:mt-0">
              {language === 'en' ? 'Powered by' : 'পরিচালিত'}{' '}
              <Link to="/" className="text-emerald-400 hover:text-emerald-300">MotoPotti</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
